// Fonctions utilitaires pour la manipulation des couleurs

/**
 * Éclaircit une couleur en format hexadécimal
 * @param {string} color - Couleur au format hexadécimal (par exemple '#3B82F6')
 * @param {number} amount - Quantité d'éclaircissement (0-255)
 * @returns {string} - Couleur éclaircie au format hexadécimal
 */
export const lightenColor = (color, amount) => {
  if (!color) return '#3B82F6';
  
  // Supprimer le # si présent
  color = color.replace(/^#/, '');
  
  // Convertir la couleur en valeurs RGB
  let r = parseInt(color.substring(0, 2), 16);
  let g = parseInt(color.substring(2, 4), 16);
  let b = parseInt(color.substring(4, 6), 16);
  
  // Éclaircir chaque composante
  r = Math.min(255, r + amount);
  g = Math.min(255, g + amount);
  b = Math.min(255, b + amount);
  
  // Convertir en hexadécimal et ajouter les zéros en préfixe si nécessaire
  const rHex = r.toString(16).padStart(2, '0');
  const gHex = g.toString(16).padStart(2, '0');
  const bHex = b.toString(16).padStart(2, '0');
  
  // Retourner la couleur au format hexadécimal
  return `#${rHex}${gHex}${bHex}`;
};

/**
 * Assombrit une couleur en format hexadécimal
 * @param {string} color - Couleur au format hexadécimal (par exemple '#3B82F6')
 * @param {number} amount - Quantité d'assombrissement (0-255)
 * @returns {string} - Couleur assombrie au format hexadécimal
 */
export const darkenColor = (color, amount) => {
  if (!color) return '#3B82F6';
  
  // Supprimer le # si présent
  color = color.replace(/^#/, '');
  
  // Convertir la couleur en valeurs RGB
  let r = parseInt(color.substring(0, 2), 16);
  let g = parseInt(color.substring(2, 4), 16);
  let b = parseInt(color.substring(4, 6), 16);
  
  // Assombrir chaque composante
  r = Math.max(0, r - amount);
  g = Math.max(0, g - amount);
  b = Math.max(0, b - amount);
  
  // Convertir en hexadécimal et ajouter les zéros en préfixe si nécessaire
  const rHex = r.toString(16).padStart(2, '0');
  const gHex = g.toString(16).padStart(2, '0');
  const bHex = b.toString(16).padStart(2, '0');
  
  // Retourner la couleur au format hexadécimal
  return `#${rHex}${gHex}${bHex}`;
};

/**
 * Convertit une couleur RGB en HSL
 * @param {number} r - Composante rouge (0-255)
 * @param {number} g - Composante verte (0-255)
 * @param {number} b - Composante bleue (0-255)
 * @returns {Array} - Tableau [h, s, l] avec h en degrés (0-360) et s, l en pourcentage (0-100)
 */
export const rgbToHsl = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatique
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    
    h /= 6;
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
};

/**
 * Convertit une couleur HSL en RGB
 * @param {number} h - Teinte (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Luminosité (0-100)
 * @returns {Array} - Tableau [r, g, b] avec des valeurs entre 0 et 255
 */
export const hslToRgb = (h, s, l) => {
  h /= 360;
  s /= 100;
  l /= 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatique
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
};
